// src/pages/job_openings/create/CandidateConfirmation/CandidateConfirmation.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  Divider,
} from '@mui/material';
import LogoWhite from '../../../../assets/images/logos/white-bg-logo-hiacuity.png';
import {
  ITimeSlot,
  confirmTimeSlot,
  getInterviewRoundCandidateDetails,
  getInterviewRoundSlots,
} from '../../../../utils/api_service/SmartInterview';
import { getImageSrc } from '../../../../utils/helpers/image-utils';
import { RootState } from '../../../../utils/redux_store/store';

// parse path or query
function useInterviewParams() {
  const { interviewId: pId, token: pToken } =
    useParams<{ interviewId?: string; token?: string }>();
  const qs = new URLSearchParams(useLocation().search);
  const rawToken = pToken || qs.get('token') || '';
  const interviewId = pId || qs.get('interviewId') || '';
  const token = rawToken ? decodeURIComponent(rawToken) : '';
  return { interviewId, token };
}

export const CandidateConfirmation: React.FC = () => {
  const { interviewId, token } = useInterviewParams();

  // candidate/me
  const [candidateName, setCandidateName] = useState('');
  const [interviewType, setInterviewType] = useState('');
  const [slots, setSlots] = useState<ITimeSlot[]>([]);

  // picker state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<ITimeSlot | null>(null);
  const [errors, setErrors] = useState<{ selectedSlot?: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState('');
  const [candidateTimeZone, setCandidateTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  // post-confirmation state
  const [confirmed, setConfirmed] = useState(false);
  const [declined, setDeclined] = useState(false);

  // store token for confirmTimeSlot if needed
  useEffect(() => {
    if (token) {
      localStorage.setItem('authToken', token);
      sessionStorage.setItem('authToken', token);
    }
  }, [token]);

  // fetch candidate info
  useEffect(() => {
    if (!interviewId || !token) return;
    getInterviewRoundCandidateDetails(interviewId, token)
      .then((r) => {
        if (!r.ok) throw new Error(`Status ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const u = data.user;
        setCandidateName(`${u.firstName} ${u.lastName}`);
        setCandidateEmail(u.email);
        setCandidateTimeZone(u.timeZone || candidateTimeZone);
        setInterviewType(data.interviewType);
      })
      .catch(() => {
        alert('Error loading candidate information');
      });
  }, [interviewId, token]);

  // fetch slots
  useEffect(() => {
    if (!interviewId || !token) return;
    setLoading(true);
    getInterviewRoundSlots(interviewId, token)
      .then((r) => {
        if (!r.ok) throw new Error(`Status ${r.status}`);
        return r.json();
      })
      .then((data) => setSlots(data.timeSlots || []))
      .catch(() => alert('Error loading available times'))
      .finally(() => setLoading(false));
  }, [interviewId, token]);

  // group slots by day
  const grouped = useMemo(() => {
    const map: Record<string, ITimeSlot[]> = {};
    slots.forEach((s) => {
      const day = new Date(s.startTime * 1000).toISOString().split('T')[0];
      map[day] = map[day] ? [...map[day], s] : [s];
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => a.startTime - b.startTime),
    );
    return map;
  }, [slots]);

  // helpers to format
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  const fmtTime = (ts: number) =>
    new Date(ts * 1000).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  // confirm handler
  const handleConfirm = () => {
    if (!selectedSlot) {
      setErrors({ selectedSlot: true });
      return;
    }
    setLoading(true);
    confirmTimeSlot(
      {
        interviewStartTime: selectedSlot.startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      interviewId,
      token,
    )
      .then((r) => {
        if (!r.ok) return r.json().then((e) => Promise.reject(e));
        setConfirmed(true);
      })
      .catch(() => alert('Failed to confirm interview'))
      .finally(() => setLoading(false));
  };

  // decline handler
  const handleDecline = () => {
    setDeclined(true);
  };

  const companyLogo = useSelector((state: RootState) => state.logo.companyLogo);

  // invalid link
  if (!token || !interviewId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            textAlign: 'center',
            maxWidth: 400,
            width: '100%',
          }}
        >
          <Typography variant="h6" sx={{ color: 'error.main', mb: 2, fontWeight: 600 }}>
            Invalid Link
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This link seems invalid or expired. Please check your email or contact support.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // initial loader
  if (loading && !slots.length && !confirmed && !declined) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2, color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary">
            Loading interview details…
          </Typography>
        </Box>
      </Box>
    );
  }

  // --- DECLINED UI ---
  if (declined) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 440,
            width: '100%',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
            You’ve declined this interview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you change your mind, this link will expire in a few hours.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ minWidth: 120, textTransform: 'none', borderRadius: 2 }}
          >
            Refresh
          </Button>
        </Paper>
      </Box>
    );
  }

  // --- CONFIRMED UI ---
  if (confirmed && selectedSlot) {
    const dayKey = new Date(selectedSlot.startTime * 1000)
      .toISOString()
      .split('T')[0];
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            p: 4,
            maxWidth: 440,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main', mb: 2 }}>
            Thank you, {candidateName}!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            Your interview is scheduled for:
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {fmtDate(dayKey)} at {fmtTime(selectedSlot.startTime)}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // --- DEFAULT: show picker ---
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        minHeight: '100vh',
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 680,
          width: '100%',
          borderRadius: 3,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* Header card */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box
            component="img"
            src={getImageSrc(companyLogo, LogoWhite)}
            sx={{ height: 40, objectFit: 'contain' }}
            alt="Company Logo"
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Hi, {candidateName || 'there'}
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: 'primary.dark',
                color: 'white',
                fontWeight: 700,
                fontSize: 20,
              }}
            >
              {candidateName.charAt(0) || '?'}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {candidateName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {candidateEmail}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Interview Type:{' '}
              </Box>
              {interviewType}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Time Zone:{' '}
              </Box>
              {candidateTimeZone}
            </Typography>
          </Box>
        </Paper>

        <Typography variant="body2" color="text.primary">
          You’re invited to a <strong>{interviewType}</strong>. Please select your preferred time:
        </Typography>

        {/* Date picker */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            borderTop: 1,
            borderColor: 'divider',
            pt: 3,
            gap: 4,
          }}
        >
          <Box sx={{ flex: 1, width: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
              Select Date
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 240, overflowY: 'auto' }}>
              {Object.keys(grouped)
                .sort()
                .map((d) => {
                  const isDateSelected = selectedDate === d;
                  const availableCount = grouped[d].filter((s) => !s.isBooked).length;
                  return (
                    <Box
                      component="button"
                      type="button"
                      key={d}
                      onClick={() => {
                        setSelectedDate(d);
                        setSelectedSlot(null);
                        setErrors({});
                      }}
                      sx={{
                        p: 1.5,
                        textAlign: 'left',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isDateSelected ? 'primary.main' : 'divider',
                        bgcolor: isDateSelected ? 'primary.50' : 'background.paper',
                        color: isDateSelected ? 'primary.main' : 'text.primary',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: isDateSelected ? 'primary.main' : 'grey.400',
                          bgcolor: isDateSelected ? 'primary.50' : 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {fmtDate(d)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {availableCount} slot{availableCount !== 1 ? 's' : ''} available
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
            {!Object.keys(grouped).length && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No available dates found
              </Typography>
            )}
          </Box>

          {/* TIME SLOTS PICKER */}
          {selectedDate && (
            <Box sx={{ flex: 1.5, width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
                Select Time
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                  maxHeight: 240,
                  overflowY: 'auto',
                }}
              >
                {grouped[selectedDate]
                  .filter((s) => !s.isBooked)
                  .map((s) => {
                    const isSelected = selectedSlot?.startTime === s.startTime;
                    return (
                      <Box
                        component="button"
                        type="button"
                        key={s.startTime}
                        onClick={() => {
                          setSelectedSlot(s);
                          setErrors({});
                        }}
                        sx={{
                          p: 1.5,
                          textAlign: 'center',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: isSelected ? 'success.main' : 'divider',
                          bgcolor: isSelected ? 'success.50' : 'background.paper',
                          color: isSelected ? 'success.dark' : 'text.primary',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: isSelected ? 'success.main' : 'grey.400',
                            bgcolor: isSelected ? 'success.50' : 'action.hover',
                          },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fmtTime(s.startTime)}
                        </Typography>
                        {s.endTime && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            to {fmtTime(s.endTime)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
              </Box>
              {grouped[selectedDate].filter((s) => !s.isBooked).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No available time slots for this date
                </Typography>
              )}
            </Box>
          )}

          {errors.selectedSlot && (
            <Typography variant="caption" color="error.main" sx={{ mt: 1 }}>
              Please select a time slot to continue
            </Typography>
          )}
        </Box>

        <Divider />

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDecline}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedSlot || loading}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {loading ? 'Confirming…' : 'Confirm Interview'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
export default CandidateConfirmation;
